/**
 * MODULE: _shared/frontend-shell/src/components/AgenticModeSwitcher.tsx
 * PURPOSE: Traditional TopBar coexistence control — match Agentic ModeSwitcher chrome
 * DEPENDENCIES: lucide-react, agenticWorkspaceUrl
 * PUBLIC_API: AgenticModeSwitcher
 * SIDE_EFFECTS: Navigates away to Agentic (same tab)
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~95 / 200 lines
 * RECENT: 2026-08-06 [2.2.2137] — Kept `shrink-0`; TopBar brand cluster owns resize visibility.
 * RECENT_PRIOR: 2026-08-03 [2.2.2022] — Open Agentic without confirm; left-of-brand icon pill.
 * RECENT_PRIOR: 2026-07-30 [2.2.1844] — Session handoff via buildAgenticHomeUrl
 */
import { ArrowUpRight, LayoutDashboard, Sparkles } from 'lucide-react';
import { buildAgenticHomeUrl } from '../lib/agenticWorkspaceUrl';

export function AgenticModeSwitcher({ compact = false }: { compact?: boolean }) {
  const openAgentic = () => {
    window.location.href = buildAgenticHomeUrl({ from: 'workspace' });
  };

  return (
    <div
      className={`inline-flex shrink-0 items-center rounded-full border border-gray-300 bg-gray-200/80 p-0.5 dark:border-white/20 dark:bg-gray-900/80 ${
        compact ? 'text-[11px]' : 'text-xs'
      }`}
      role="group"
      aria-label="Workspaces. Traditional is current. Open Agentic leaves this tab."
      data-testid="agentic-mode-switcher"
    >
      <span
        className={`inline-flex items-center rounded-full bg-white font-medium text-gray-900 shadow-sm dark:bg-gray-600 dark:text-white ${
          compact ? 'p-2' : 'gap-1 px-2 py-1.5 sm:gap-1.5 sm:px-2.5'
        }`}
        aria-current="page"
        title="You are in Traditional Workspace"
        data-testid="agentic-mode-switcher-traditional-here"
      >
        <LayoutDashboard className={`${compact ? 'h-4 w-4' : 'h-3.5 w-3.5'} shrink-0`} aria-hidden />
        {!compact && (
          <>
            <span className="hidden sm:inline">Traditional</span>
            <span className="rounded bg-primary-50 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary-700 dark:bg-indigo-500/20 dark:text-indigo-300">
              Here
            </span>
          </>
        )}
      </span>
      <button
        type="button"
        onClick={openAgentic}
        className={`inline-flex items-center rounded-full text-gray-600 transition-colors hover:text-gray-900 dark:text-indigo-300 dark:hover:text-white ${
          compact ? 'p-2' : 'gap-1 px-2 py-1.5 sm:gap-1.5 sm:px-2.5'
        }`}
        title="Open Agentic Workspace — leaves Traditional in this tab"
        aria-label="Open Agentic Workspace — leaves Traditional in this tab"
        data-testid="agentic-mode-switcher-open"
      >
        <Sparkles
          className={`${compact ? 'h-4 w-4' : 'h-3.5 w-3.5'} shrink-0 text-primary-600 dark:text-indigo-300`}
          aria-hidden
        />
        {!compact && (
          <>
            <span className="hidden sm:inline">Agentic</span>
            <ArrowUpRight className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
          </>
        )}
      </button>
    </div>
  );
}
